import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import awardPoint from '../assets/customicons/awardPoint.png';
import trophyPoint from '../assets/customicons/trophyPoint.png';
import Avatar from '../components/cards/Avatar';
import LeaderboardChart from '../components/Leaderboard/LeaderboardChart';
import LeaderboardRow from '../components/Leaderboard/LeaderboardRow';
import Staircase from '../components/Leaderboard/Staircase';
import AppHeader from '../components/nav/AppHeader';
import BottomNav from '../components/nav/BottomNav';
import { getLevelFromPoints } from '../consts/avatarMap';
import { sendReactGAPageView } from '../consts/helpers';
import useAuthUser from '../hooks/useAuthUser';

export default function LeaderboardPage() {
	const { pathname } = useLocation();
	useEffect(() => {
        sendReactGAPageView(pathname, 'Leaderboard');
    }, [pathname])

    var { userid }  = useAuthUser();

    // Flags
    const [isLoading, setIsLoadingLeaderboard] = useState(true);
    const [isSuccessful, setSuccessfulLeaderboard] = useState(true);

    // Variables
    const [mainPlayer, setMainPlayer] = useState({});
    const [topPlayers, setTopPlayers] = useState([]);
    const [nearbyPlayers, setNearbyPlayers] = useState([]);
    const [playerPerLevelCount, setPlayerPerLevelCount] = useState([]);

    const fetchLeaderboard = () => {
        setIsLoadingLeaderboard(true);
        const url = process.env.REACT_APP_LEADERBOARD_LAMBDA;
        fetch(url, {
            method: 'POST',
            body: JSON.stringify({
                "httpMethod": "GET",
                "resourcePath": "/leaderboard",
                "uid": userid
            }),
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        }).then(response => {
            return response.json();
        }).then(data => {
            let results = JSON.parse(data?.body || '{}');
            setSuccessfulLeaderboard(results?.isSuccess);
            // console.log(results)

            // Set variables
            setTopPlayers(results?.topPlayers);
            setNearbyPlayers(results?.nearbyPlayers);
            setMainPlayer(results?.nearbyPlayers.filter(u => u?.isSelected)[0]);
            setPlayerPerLevelCount(results?.playerPerLevelCount);
        }).catch(error => {
            console.error('There has been a problem with your fetch operation:', error);
            return {};
        }).finally(() => {
            setIsLoadingLeaderboard(false);
        });
    }

    useEffect(() => {
        if (userid) fetchLeaderboard();
    }, [userid]);

    return (
        <>
            <AppHeader />
            <Box className="spread-x" sx={{ mb: 1 }}>
                <Typography variant="h5" gutterBottom>Leaderboard</Typography>
                <img src={trophyPoint} style={{ height: 35 }} alt="coin icon" />
            </Box>
            {
                isLoading ?
                    <Box className='centered-container' sx={{ flex: 1 }}>
                        <CircularProgress color='secondary' />
                    </Box>
                :
                isSuccessful ?
                <Stack spacing={3}>
                    {/* Top 3 Players */}
                    {topPlayers && 
                        <LeaderboardChart topPlayers={topPlayers}/>
                    }

                    <Box sx={{ borderRadius: '42px', backgroundColor: '#332768' }}>
                        {/* User Rank */}
                        { mainPlayer &&
                        <Box className="leaderboard-rankcard">
                            <Typography variant="h6">
                                {mainPlayer?.rank && 
                                <>You are in {getOrdinalNumber(mainPlayer?.rank)} place!</>
                                } 
                            </Typography>

                            <Typography variant="body1">
                                Rank: <Avatar level={ getLevelFromPoints(mainPlayer?.totalPoints) } getImage={false} />
                            </Typography>
                        </Box>
                        }

                        {/* Nearby Players */}
                        {nearbyPlayers && 
                        <Stack spacing={1} sx={{ padding: '10px' }}>
                            {
                                nearbyPlayers[0]?.rank !== 1 &&
                                <LeaderboardRowTransition position='top' />
                            }
                            {nearbyPlayers.map((player, index) => (
                                <LeaderboardRow
                                    key={index}
                                    rank={player?.rank}
                                    name={player?.username}
                                    points={player?.totalPoints}
                                    isSelected={player?.isSelected}
                                />
                            ))}          
                            {
                                nearbyPlayers[nearbyPlayers.length-1]?.rank <= playerPerLevelCount.reduce((prev, curr) => prev + curr, 0) &&
                                <LeaderboardRowTransition position='bottom' />
                            }
                        </Stack>
                        }
                    </Box>

                    {/* Avatar Staircase */}
                    <Staircase avatarCount={playerPerLevelCount} actualAvatar={ getLevelFromPoints(mainPlayer?.totalPoints) } />
                </Stack>
                :
                <Box className='centered-container' sx={{ flex: 1, textAlign: 'center', gap: 2 }}>
                    <Typography variant="h6">
                        Sorry! 
                    </Typography>
                    <Typography variant="body1">
                        The leaderboard is currently unavailable. Please come back another time.
                    </Typography>
                    <img src={awardPoint} alt="award-point" style={{ width: 100 }} />
                </Box>
            }
            <BottomNav />
        </>
    );
}

const getOrdinalNumber = (num) => {
    var numref = num-1;
    const suffix = ["st","nd","rd"][numref % 10] && ![11, 12, 13].includes(num % 100) ? ["st","nd","rd"][numref % 10] : "th";
    return <span>{num}<sup>{suffix}</sup></span>;
}

const LeaderboardRowTransition = ({ position }) => {
    return <Box 
        className="leaderboard-row" 
        sx={{
            border: 'none',
            minHeight: '45px',
            background: `linear-gradient(${position==='bottom' ? '0' : '180'}deg, rgba(225,225,225,0) 20%, rgba(225,225,225,0.8) 100%)`
        }}
    />
}